// ส่งออกกระดาษคำตอบเป็น PNG ให้ครูวางใน Word แล้วเขียนครึ่งล่างเอง
// เฉพาะกระดาษสำรองแบบครึ่งแผ่น — ทุกแผ่นเหมือนกัน วางภาพเดียวพิมพ์กี่ชุดก็ได้ (แผ่นรายคนมีแถบรหัสคนละชุด)
//
// SVG ที่วาดเป็นภาพ (img/canvas) โหลดไฟล์ภายนอกไม่ได้ทั้งฟอนต์และรูป จึงต้องฝังเป็น data URL ก่อน
// และเขียน DPI ลง PNG (chunk pHYs) ไม่งั้น Word ถือว่า 96 dpi แล้วภาพกว้าง 65 ซม.
import React from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import AnswerSheet from './AnswerSheet'
import { PAGE, frameFor, HALF_VERSION } from './sheetLayout'

export const PNG_DPI = 300
const MM_PER_INCH = 25.4

const FONTS = [
  { file: 'THSarabunNew.ttf', weight: 'normal' },
  { file: 'THSarabunNew-Bold.ttf', weight: 'bold' },
]

const toDataUrl = async (url) => {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`โหลด ${url} ไม่สำเร็จ`)
  const blob = await res.blob()
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result)
    r.onerror = reject
    r.readAsDataURL(blob)
  })
}

// ---- PNG pHYs ----
const CRC_TABLE = (() => {
  const t = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    t[n] = c >>> 0
  }
  return t
})()
export const crc32 = (bytes) => {
  let c = 0xffffffff
  for (const b of bytes) c = CRC_TABLE[(c ^ b) & 0xff] ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

// แทรก pHYs ต่อจาก IHDR (signature 8 + IHDR 25 ไบต์) — canvas.toBlob ไม่ใส่ให้
export const withDpi = (png, dpi) => {
  const ppm = Math.round(dpi / 0.0254)
  const chunk = new Uint8Array(21)
  const view = new DataView(chunk.buffer)
  view.setUint32(0, 9)
  chunk.set([0x70, 0x48, 0x59, 0x73], 4)   // 'pHYs'
  view.setUint32(8, ppm)
  view.setUint32(12, ppm)
  chunk[16] = 1                           // หน่วยเป็นเมตร
  view.setUint32(17, crc32(chunk.subarray(4, 17)))
  const out = new Uint8Array(png.length + chunk.length)
  out.set(png.subarray(0, 33), 0)
  out.set(chunk, 33)
  out.set(png.subarray(33), 33 + chunk.length)
  return out
}

// ครึ่งบนถึงก่อนเส้นแบ่งพื้นที่อัตนัย — รวมสี่เหลี่ยมมุมล่าง แถบรหัสแถวล่าง และคำเตือนใต้แถบ
export const HALF_CROP_MM = frameFor(HALF_VERSION).written - 1

export async function spareHalfPng({ exam, questions, room, school }) {
  const markup = renderToStaticMarkup(
    <AnswerSheet exam={exam} questions={questions} room={room} school={school} spare writing="blank" />
  )
  const doc = new DOMParser().parseFromString(markup, 'image/svg+xml')
  const svg = doc.documentElement
  const width = Math.round((PAGE.width / MM_PER_INCH) * PNG_DPI)
  const height = Math.round((HALF_CROP_MM / MM_PER_INCH) * PNG_DPI)
  svg.setAttribute('viewBox', `0 0 ${PAGE.width} ${HALF_CROP_MM}`)
  svg.setAttribute('width', width)
  svg.setAttribute('height', height)

  const fonts = await Promise.all(FONTS.map(async (f) => (
    `@font-face { font-family: 'THSarabun'; font-weight: ${f.weight}; font-style: normal; `
    + `src: url(${await toDataUrl(`${process.env.PUBLIC_URL}/fonts/${f.file}`)}) format('truetype'); }`
  )))
  const style = doc.createElementNS('http://www.w3.org/2000/svg', 'style')
  style.textContent = fonts.join('\n')
  svg.insertBefore(style, svg.firstChild)

  // ตราโรงเรียน — โหลดไม่ได้ (เช่นติด CORS) ให้ตัดทิ้ง ดีกว่าส่งออกไม่ได้ทั้งแผ่น
  for (const img of Array.from(svg.getElementsByTagName('image'))) {
    const href = img.getAttribute('href')
    try {
      if (href && !href.startsWith('data:')) img.setAttribute('href', await toDataUrl(href))
    } catch {
      img.remove()
    }
  }

  const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' }))
  try {
    const image = await new Promise((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('วาดภาพกระดาษคำตอบไม่สำเร็จ'))
      el.src = url
    })
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, width, height)
    ctx.drawImage(image, 0, 0, width, height)
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
    const png = new Uint8Array(await blob.arrayBuffer())
    return new Blob([withDpi(png, PNG_DPI)], { type: 'image/png' })
  } finally {
    URL.revokeObjectURL(url)
  }
}
